import { defineComponent } from 'vue'
import type { IProviderImportStepProps } from '../types/stepProps'
import Icon from 'components/icon'

import Section from 'components/section'
import InfoCard from '@/modules/asset/components/info-card'
import EyeIcon from 'assets/img/svg/icons/eye.svg?url'
import ShieldIcon from 'assets/img/svg/icons/shield.svg?url'
import { Spinner } from 'components/spinner'
import { useResponsive } from '~/composables/useResponsive'

import Button from 'components/button'
import EmptyState from 'components/empty-state'
import SearchIcon from 'assets/img/svg/icons/search.svg?url'
import EnvelopeIcon from 'assets/img/svg/icons/envelope.svg?url'
import { ResponsiveSearchFilters, useSearchFilters } from 'components/responsive-search-filters'
import type { ITabProps } from 'components/tabs/types'
import { sendFeaturebaseFeedback } from '@/integrations/featurebase/utils'
import { CountryFlag } from 'components/country-flag'
import TinyBadge from 'components/tiny-badge'
import type { IntegrationProvider } from 'findef'

const PROVIDER_TABS: ITabProps[] = [
	{ title: 'All Providers', value: 'all', active: true },
	{ title: 'Banks', value: 'bank' },
	{ title: 'Digital Share Ledgers', value: 'digital_share_ledger' },
	{ title: 'Crypto', value: 'crypto' },
	{ title: 'Other', value: 'other' }
]

const PROVIDER_CATEGORY_KEYWORDS: Record<string, string[]> = {
	'bank': ['retail', 'commercial', 'creditUnion', 'private', 'challenger', 'neobank', 'snl'],
	'digital_share_ledger': ['digital_share_ledger'],
	'crypto': ['crypto'],
	'other': ['other', 'investment']
}

export const Disclaimers = () => {
	return (
		<div class="flex flex-col md:flex-row gap-3 md:gap-4">
			<InfoCard
				heightClass="h-auto"
				class="w-full"
				title="Read Only Access for Accounts"
				subtitle="Information is only used to display your assets & liabilities."
				variant="blue"
				titleClass="text-primary-500 text-sm font-bold leading-[21px]"
				subtitleClass="text-gray-700 text-xs font-normal"
				icon={{
					src: EyeIcon,
					fill: 'currentColor',
					size: 14
				}}
			/>
			<InfoCard
				heightClass="h-auto"
				class="w-full"
				title="No Trading or Withdrawals"
				subtitle="Findex can’t perform any transactions on your accounts."
				variant="blue"
				titleClass="text-primary-500 text-sm font-bold leading-[21px]"
				subtitleClass="text-gray-700 text-xs font-normal"
				icon={{
					src: ShieldIcon,
					fill: 'currentColor',
					size: 14
				}}
			/>
		</div>
	)
}

const ProviderCard = defineComponent<{ provider: IntegrationProvider; onClick?: any }>(
	(props) => {
		const responsive = useResponsive();
		const country = props.provider.country === 'SWE' ? "SE" : props.provider.country;
		return () => {
			return (
				<div
					class="w-full h-full px-4 py-3 rounded-xs border border-gray-300 flex justify-between items-center gap-3 hover:bg-primary-100 hover:border-primary-500 transition-shadow"
					style={{
						cursor: 'pointer'
					}}
				>
					<div class="flex items-center justify-center gap-2 overflow-hidden">
						<div class="min-w-6 w-6 h-6 bg-white border border-gray-300 justify-center items-center flex text-gray-500">
							{props.provider.iconUrl ? (
								props.provider.iconUrl.endsWith('.svg') ? (
									<Icon src={props.provider.iconUrl} size="1.5rem" color="black" fill="black" />
								) : (
									<img
										style={{ aspectRatio: '1 / 1', height: '16px', minWidth: '16px' }}
										src={props.provider.iconUrl}
									/>
								)
							) : null}
						</div>
						<span class="text-gray-900 text-sm font-medium text-center leading-4 whitespace-nowrap text-ellipsis overflow-hidden">
							{props.provider.displayName || props.provider.name}
						</span>
					</div>
					{
						country && (
							!responsive.isSmallPhone.value && (
								<TinyBadge size="m" variant="gray">
									<span class="flex items-center gap-2 text-gray-500 text-xs font-semibold leading-4 cursor-pointer">
										<CountryFlag country={country} />
										{country}
									</span>
								</TinyBadge>
							)
						)
					}
				</div>
			)
		}
	},
	{ props: ['provider'] }
)
export const SelectProviderStep = defineComponent<IProviderImportStepProps>(
	(props) => {
		const responsive = useResponsive();

		const providerFilters = useSearchFilters<IntegrationProvider>({
			items: props.hook.providers,
			categoryKeywords: PROVIDER_CATEGORY_KEYWORDS,
			categoryTypeField: 'providerType'
		})

		return () => {
			const trimmedQuery = providerFilters.queryString.value.trim()
			const headline = trimmedQuery.length > 0 ? trimmedQuery : 'your provider'
			return (
				<div class="w-full h-full flex flex-col items-center px-4">
					<div class="flex flex-1 flex-col gap-6 max-w-[944px] w-full pb-4 md:pb-0">
						<div class="border border-gray-300 p-4 md:p-8 w-full h-full space-y-4">
							<div class="flex flex-col space-y-6 min-h-[400px] h-full">
								<Section class="space-y-2">
									<div class="text-gray-900 text-lg font-semibold font-['Inter'] leading-[18px]">
										Connect Provider Accounts
									</div>
									<div class="text-gray-500 text-sm font-normal font-['Inter'] leading-[21px]">
										Automatically track assets & liabilities by connecting to providers.
									</div>
								</Section>

								<ResponsiveSearchFilters
									filters={providerFilters}
									tabs={PROVIDER_TABS}
									options={{
										searchPlaceholder: 'Search for providers'
									}}
								/>

								{props.hook.isFetchingProviders.value ? (
									<div class="flex items-center justify-center w-full h-full">
										<Spinner />
									</div>
								) : providerFilters.filteredItems.value.length > 0 ? (
									<div class="grid grid-cols-1 md:grid-cols-2 gap-2 gap-y-2 w-full justify-between content-between">
										{providerFilters.filteredItems.value.map((it) => {
											return (
												<ProviderCard
													provider={it}
													onClick={() => props.hook.selectProvider(it.id)}
													key={`provider-${it.id}-${it.name}`}
												/>
											)
										})}
									</div>
								) : (
									<div class="w-full h-full flex items-center justify-center">
										<EmptyState
											title="No Result Found"
											subtitle={`We build integrations based on demand from users like you. Request ${headline} and join others asking for this provider.`}
											icon={SearchIcon}
											iconContainer={{
												iconBgClass: 'bg-gray-100 text-gray-400 mb-3',
												size: responsive.isSmall.value ? 'sm' : 'md',
												containerSize: responsive.isSmall.value ? '32' : '64'
											}}
											style={{ width: '100%', maxWidth: '460px' }}
											subtitleAlign="center"
											size="s"
											action={() => (
												<Button
													onClick={() => sendFeaturebaseFeedback('Providers & Integrations')}
													icon={{
														src: EnvelopeIcon,
														size: 14,
														fill: 'white',
														stroke: 'none',
													}}
													class="px-5"
												>
													Request Integration
												</Button>
											)}
										/>
									</div>
								)}
							</div>
						</div>
						<Disclaimers />
					</div>
				</div>
			)
		}
	},
	{ props: ['hook'] }
)
